import { AlertTriangle } from 'lucide-react'
import { ErrorBoundary } from 'react-error-boundary'
import { ChatAiComponent, JsonObject } from '../types'
import { AiAreaChart } from './ai-area-chart/AiAreaChart'
import { AiAreaChartSchema } from './ai-area-chart/types-ai-area-chart'
import { AiBarChart } from './ai-bar-chart/AiBarChart'
import { AiBarChartSchema } from './ai-bar-chart/types-ai-bar-chart'
import { AiLineChart } from './ai-line-chart/AiLineChart'
import { AiLineChartSchema } from './ai-line-chart/types-ai-line-chart'
import { AiPieChart } from './ai-pie-chart/AiPieChart'
import { AiPieChartSchema } from './ai-pie-chart/types-ai-pie-chart'
import { AiRadarChart } from './ai-radar-chart/AiRadarChart'
import { AiRadarChartSchema } from './ai-radar-chart/types-ai-radar-chart'
import { AiRadialBarChart } from './ai-radial-bar-chart/AiRadialBarChart'
import { AiRadialBarChartSchema } from './ai-radial-bar-chart/types-ai-radial-bar-chart'
import { AiTable } from './ai-table/AiTable'
import { AiTableSchema } from './ai-table/types-ai-table'

function isRecord(value: unknown): value is JsonObject {
  return value !== null && typeof value === 'object'
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string')
}

function isAiTableSchema(schema: unknown): schema is AiTableSchema {
  if (!isRecord(schema)) return false

  return (
    schema.componentType === 'ai-table' &&
    typeof schema.title === 'string' &&
    Array.isArray(schema.columns) &&
    Array.isArray(schema.rows) &&
    isRecord(schema.meta)
  )
}

function isAiLineChartSchema(schema: unknown): schema is AiLineChartSchema {
  if (!isRecord(schema)) return false

  const config = isRecord(schema.config) ? schema.config : null

  return (
    schema.componentType === 'ai-line-chart' &&
    typeof schema.title === 'string' &&
    Array.isArray(schema.data) &&
    Boolean(config) &&
    typeof config?.categoryKey === 'string' &&
    isStringArray(config.lineDataKeys) &&
    isRecord(config.seriesConfig)
  )
}

function isAiBarChartSchema(schema: unknown): schema is AiBarChartSchema {
  if (!isRecord(schema)) return false

  const config = isRecord(schema.config) ? schema.config : null

  return (
    schema.componentType === 'ai-bar-chart' &&
    typeof schema.title === 'string' &&
    Array.isArray(schema.data) &&
    Boolean(config) &&
    typeof config?.categoryKey === 'string' &&
    isStringArray(config.barDataKeys) &&
    isRecord(config.seriesConfig)
  )
}

function isAiAreaChartSchema(schema: unknown): schema is AiAreaChartSchema {
  if (!isRecord(schema)) return false

  const config = isRecord(schema.config) ? schema.config : null

  return (
    schema.componentType === 'ai-area-chart' &&
    typeof schema.title === 'string' &&
    Array.isArray(schema.data) &&
    Boolean(config) &&
    typeof config?.categoryKey === 'string' &&
    isStringArray(config.areaDataKeys) &&
    isRecord(config.seriesConfig)
  )
}

function isAiPieChartSchema(schema: unknown): schema is AiPieChartSchema {
  if (!isRecord(schema)) return false

  const config = isRecord(schema.config) ? schema.config : null

  return (
    schema.componentType === 'ai-pie-chart' &&
    typeof schema.title === 'string' &&
    Array.isArray(schema.data) &&
    Boolean(config) &&
    typeof config?.categoryKey === 'string' &&
    typeof config.valueKey === 'string' &&
    isRecord(config.categoryConfig)
  )
}

function isAiRadarChartSchema(schema: unknown): schema is AiRadarChartSchema {
  if (!isRecord(schema)) return false

  const config = isRecord(schema.config) ? schema.config : null

  return (
    schema.componentType === 'ai-radar-chart' &&
    typeof schema.title === 'string' &&
    Array.isArray(schema.data) &&
    Boolean(config) &&
    typeof config?.categoryKey === 'string' &&
    isStringArray(config.radarDataKeys) &&
    isRecord(config.seriesConfig)
  )
}

function isAiRadialBarChartSchema(schema: unknown): schema is AiRadialBarChartSchema {
  if (!isRecord(schema)) return false

  const config = isRecord(schema.config) ? schema.config : null

  return (
    schema.componentType === 'ai-radial-bar-chart' &&
    typeof schema.title === 'string' &&
    Array.isArray(schema.data) &&
    Boolean(config) &&
    typeof config?.categoryKey === 'string' &&
    typeof config.valueKey === 'string' &&
    isRecord(config.categoryConfig)
  )
}

function InvalidAiComponentCard({ componentType, reason }: { componentType: string; reason: string }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border bg-background/80 p-4 text-sm shadow-sm">
      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" />
      <div>
        <div className="font-medium">{componentType}</div>
        <div className="mt-1 text-muted-foreground">{reason}</div>
      </div>
    </div>
  )
}

function AiComponentCard({ component }: { component: ChatAiComponent }) {
  const componentType = component.componentType

  switch (component.componentType) {
    case 'ai-table':
      return isAiTableSchema(component.schema) ? (
        <AiTable schema={component.schema} />
      ) : (
        <InvalidAiComponentCard componentType={component.componentType} reason="Invalid AI table schema." />
      )
    case 'ai-line-chart':
      return isAiLineChartSchema(component.schema) ? (
        <AiLineChart schema={component.schema} />
      ) : (
        <InvalidAiComponentCard componentType={component.componentType} reason="Invalid AI line chart schema." />
      )
    case 'ai-bar-chart':
      return isAiBarChartSchema(component.schema) ? (
        <AiBarChart schema={component.schema} />
      ) : (
        <InvalidAiComponentCard componentType={component.componentType} reason="Invalid AI bar chart schema." />
      )
    case 'ai-area-chart':
      return isAiAreaChartSchema(component.schema) ? (
        <AiAreaChart schema={component.schema} />
      ) : (
        <InvalidAiComponentCard componentType={component.componentType} reason="Invalid AI area chart schema." />
      )
    case 'ai-pie-chart':
      return isAiPieChartSchema(component.schema) ? (
        <AiPieChart schema={component.schema} />
      ) : (
        <InvalidAiComponentCard componentType={component.componentType} reason="Invalid AI pie chart schema." />
      )
    case 'ai-radar-chart':
      return isAiRadarChartSchema(component.schema) ? (
        <AiRadarChart schema={component.schema} />
      ) : (
        <InvalidAiComponentCard componentType={component.componentType} reason="Invalid AI radar chart schema." />
      )
    case 'ai-radial-bar-chart':
      return isAiRadialBarChartSchema(component.schema) ? (
        <AiRadialBarChart schema={component.schema} />
      ) : (
        <InvalidAiComponentCard componentType={component.componentType} reason="Invalid AI radial bar chart schema." />
      )
    default:
      return <InvalidAiComponentCard componentType={componentType} reason="Unsupported AI component type." />
  }
}

export function AiComponentRenderer({ components }: { components: ChatAiComponent[] }) {
  if (components.length === 0) return null

  return (
    <div className="flex w-full flex-col gap-4">
      {components.map((component, index) => (
        <ErrorBoundary
          key={`${component.componentType}-${index}`}
          fallbackRender={() => (
            <InvalidAiComponentCard
              componentType={component.componentType}
              reason="This AI component could not be rendered."
            />
          )}
        >
          <div className="w-full overflow-hidden rounded-xl border bg-background/80 p-4 shadow-sm">
            <AiComponentCard component={component} />
          </div>
        </ErrorBoundary>
      ))}
    </div>
  )
}
